// REQUEST-EXPIRY-VISIBILITY1 Phase B — client-side request state derivation.
//
// The backend deliberately emits no computed expiry field (Holly rejected
// is_expired for this ticket), so every surface derives state from the raw
// columns already on the wire: status + expires_at + quote_count.
//
// Display only. Nothing here writes status, and nothing here gates quote
// submission or acceptance — those keep reading request.status directly.

export type RequestState = 'Active' | 'Expired' | 'Closed';

/**
 * Closed:  status !== 'open'
 * Expired: status === 'open' AND expires_at <= now
 * Active:  status === 'open' AND expires_at > now
 *
 * Order matters. Closed is checked first, so a closed request that is also
 * past its expires_at renders Closed, not Expired.
 *
 * expires_at is nullable. A null must NOT read as expired — an open request
 * with no expiry set has not stopped being visible, so it renders Active.
 * An unparseable date is treated the same way, for the same reason: never
 * declare a request dead on the strength of a value we could not read.
 *
 * Parsing note: quote_requests.expires_at is `timestamp without time zone`,
 * but pg parses it into a Date and res.json() ISO-serializes that, so the
 * value reaches the browser offset-tagged ("...Z"). new Date() parses it
 * unambiguously here. See the ticket report for the upstream server-timezone
 * caveat that this cannot fix client-side.
 */
export function getRequestState(
  status: string | null | undefined,
  expiresAt: string | null | undefined
): RequestState {
  if (status !== 'open') return 'Closed';
  if (!expiresAt) return 'Active';
  const expiryMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiryMs)) return 'Active';
  return expiryMs <= Date.now() ? 'Expired' : 'Active';
}

/**
 * quote_count arrives as a STRING — pg maps bigint to string and the backend
 * applies no ::int cast (matching the sibling route at src/quotes.ts:764).
 * Comparing the raw value is a silent trap: "0" is truthy, so `!quote_count`
 * and `quote_count === 0` both fail to spot a request that got nothing.
 * Always route through this before any numeric comparison.
 */
export function getQuoteCount(rawCount: string | number | null | undefined): number {
  const parsed = Number(rawCount);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The case the ticket exists for: a homeowner posted, got zero quotes, the
 * request stopped being visible to contractors, and nobody was told.
 *
 * Scope note — this covers zero-quote Expired AND zero-quote Closed, not
 * Expired alone. On current data every past-expiry request also carries
 * status='closed', so under Closed-first precedence a strict Expired-only
 * highlight would match nothing at all. Both states mean the same thing to
 * the homeowner: the request is dead and it never drew a bid. An Active
 * request with zero quotes is deliberately excluded — it is still live and
 * can still draw one.
 */
export function isDeadWithNoQuotes(
  state: RequestState,
  rawCount: string | number | null | undefined
): boolean {
  return state !== 'Active' && getQuoteCount(rawCount) === 0;
}

/** Short absolute date for table cells. Returns an em dash for null/unparseable. */
export function formatShortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Palette for each state, shared by the admin table and the homeowner surfaces. */
export const REQUEST_STATE_COLORS: Record<RequestState, { bg: string; color: string; border: string }> = {
  Active:  { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  Expired: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  Closed:  { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
};
